import Link from 'next/link'
import styled from 'styled-components'

import ExtraLargeHeader from 'components/ExtraLargeHeader'
import Spacer from 'components/Spacer'

const HeaderContainer = styled.div`
  padding: 100px ${props => props.theme.metrics.gutterPhone}px;
  background: ${props => props.theme.colors.purple};
`

const MaxWidthContainer = styled.div`
  max-width: 750px;
  margin: 0 auto;
`

const HeaderSubtitleText = styled.h6`
  margin: 0;
  padding: 0;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
  color: ${props => props.theme.colors.white};
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: 22px;
    line-height: 30px;
  }
`

const HeaderSubtitleTextBold = styled.span`
  font-style: normal;
  font-weight: 900;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: 22px;
    line-height: 30px;
  }
`

interface ISectionProps {
  background?: string
}
const Section = styled.div<ISectionProps>`
  padding: 60px ${props => props.theme.metrics.gutterPhone}px;
  background: ${props => props.theme.colors[props.background || 'white']};
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    padding: 120px 30px;
  }
`

const MaxSectionWidthContainer = styled(MaxWidthContainer)`
  max-width: ${props => props.theme.metrics.contentMaxWidth}px;
`

const NumberedItemRow = styled.div`
  display: flex;
`

const Circle = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 36px;
  width: 36px;
  border-radius: 18px;
  background: ${props => props.theme.colors.blue5};

  padding-top: 2px; /* font correction */
  font-style: normal;
  font-weight: 900;
  font-size: 16px;
  line-height: 24px;
  color: ${props => props.theme.colors.white};
`

const NumberedItemColumn = styled.div`
  margin-top: 8px;
`

const Heading = styled.div`
  font-style: normal;
  font-weight: 800;
  font-size: 16px;
  line-height: 24px;
  text-transform: uppercase;
  color: ${props => props.theme.colors.black};
`

const StyledText = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  color: ${props => props.theme.colors.black};
`

interface IUppercaseProps {
  uppercase?: boolean
}
const StyledParagraph = styled.p<IUppercaseProps>`
  ${props => props.uppercase && 'text-transform: uppercase;'}
  margin: 0;
  padding: 0;

  padding-bottom: 30px;
  &:last-child {
    padding-bottom: 0;
  }
`

const StyledAnchor = styled.a`
  color: ${props => props.theme.colors.purple};
  text-decoration: underline;
  word-break: break-word;

  cursor: pointer;
`

const StyledUnorderedList = styled.ul`
  margin: 0;
  padding-inline-start: 20px;
  padding-inline-end: 20px;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    padding-inline-start: 40px;
    padding-inline-end: 40px;
  }
`

const StyledListItem = styled.li`
  font-style: normal;
  font-size: 16px;
  line-height: 24px;
  text-align: justify;
  color: ${props => props.theme.colors.black};
  mix-blend-mode: normal;
  opacity: 0.7;

  padding-bottom: 30px;
  &:last-child {
    padding-bottom: 0;
  }

  /* custom bullet */
  list-style: none;
  &:before {
    content: '•';
    font-size: 16px;
    vertical-align: middle;
    padding-right: 14px;
  }
`

const StyledListItemHeading = styled.span`
  font-weight: 900;
`

const LightItalicText = styled.div<IUppercaseProps>`
  font-style: italic;
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
  color: ${props => props.theme.colors.black};
  opacity: 0.5;

  ${props => props.uppercase && 'text-transform: uppercase;'}
`

const FooterContainer = styled.div`
  margin: 0 10px 10px 10px;
  padding: 30px 15px;
  background: ${props => props.theme.colors.blue5};
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    margin: 0 20px 20px 20px;
    padding: 90px 30px;
  }
`

const renderTourAppGraf = () => (
  <StyledParagraph>
    We have launched a new free app, called HomeLister Live Tours, that lets homeowners easily
    schedule and host live virtual house tours with buyers and their agents. You can use this app on
    your smartphone to tour them around your property, and answer questions as you go.&nbsp;
    <Link
      href="/virtual-home-tours"
      passHref
    >
      <StyledAnchor>Click here to learn more about this app</StyledAnchor>
    </Link>
    &nbsp;and&nbsp;
    <StyledAnchor href="http://insights.homelister.com/insights//homelister-app-instructions">
      here for a step by step instructional guide on how to use it.
    </StyledAnchor>
  </StyledParagraph>
)

const CovidPage = () => (
  <>
    <HeaderContainer>
      <MaxWidthContainer>
        <ExtraLargeHeader
          title="Covid-19 Best Practices and Guidance"
          textColor="white"
          noMargin
        />
        <Spacer size={30} />
        <HeaderSubtitleText>
          We will provide the most recent guidance we have received from each state as well as some
          individual MLSs on this page. We will update this page daily with any changes.&nbsp;
          <HeaderSubtitleTextBold>
            Please note that city and county orders may be missing.
          </HeaderSubtitleTextBold>
        </HeaderSubtitleText>
      </MaxWidthContainer>
    </HeaderContainer>
    <Section>
      <MaxSectionWidthContainer>
        <NumberedItemRow>
          <Circle>1</Circle>
          <Spacer size={8} />
          <NumberedItemColumn>
            <Heading>Open Houses</Heading>
            <Spacer size={8} />
            <StyledText>
              Many MLSs still have the open house field disabled and open houses may not be created.
              See state specific guidance below to determine whether you may hold open houses.
            </StyledText>
          </NumberedItemColumn>
        </NumberedItemRow>
        <Spacer size={30} />
        <NumberedItemRow>
          <Circle>2</Circle>
          <Spacer size={8} />
          <NumberedItemColumn>
            <Heading>In Person Tours</Heading>
            <Spacer size={8} />
            <StyledText>
              Many states are now allowing in person tours to resume. Please follow&nbsp;
              <StyledAnchor href="https://www.cdc.gov/coronavirus/2019-ncov/prevent-getting-sick/prevention.html?CDC_AA_refVal=https%3A%2F%2Fwww.cdc.gov%2Fcoronavirus%2F2019-ncov%2Fprepare%2Fprevention.html">
                CDC preventative guidelines
              </StyledAnchor>
              . We recommend requiring visitors to use booties and gloves while in your home.&nbsp;
              <StyledAnchor href="http://insights.homelister.com/insights//safe-open-houses">
                See our blog post here on our recommendations on how to conduct a house tour safely.
              </StyledAnchor>
            </StyledText>
          </NumberedItemColumn>
        </NumberedItemRow>
        <Spacer size={30} />
        <NumberedItemRow>
          <Circle>3</Circle>
          <Spacer size={8} />
          <NumberedItemColumn>
            <Heading>Virtual Tours</Heading>
            <Spacer size={8} />
            <StyledText>
              You can now use the HomeLister Live Virtual Tours app on&nbsp;
              <StyledAnchor href="https://apps.apple.com/us/app/homelister-live-tours/id1515090384">
                iOS
              </StyledAnchor>
              &nbsp;or&nbsp;
              <StyledAnchor href="https://play.google.com/store/apps/details?id=com.homelister&hl=en_US">
                Android
              </StyledAnchor>
              &nbsp;to easily conduct virtual showings. You can also get tips on virtual tours&nbsp;
              <Link
                href="/virtual-home-tours"
                passHref
              >
                <StyledAnchor>here</StyledAnchor>
              </Link>
              .
            </StyledText>
          </NumberedItemColumn>
        </NumberedItemRow>
        <Spacer size={30} />
        <NumberedItemRow>
          <Circle>4</Circle>
          <Spacer size={8} />
          <NumberedItemColumn>
            <Heading>Covid Forms</Heading>
            <Spacer size={8} />
            <StyledText>
              Many states have created Covid-19 Disclosure and/or Indemnity Forms that are either
              required or optional. You can now find a copy of your state’s form in the documents
              section of your listing dashboard.
            </StyledText>
          </NumberedItemColumn>
        </NumberedItemRow>
        <Spacer size={30} />
        <NumberedItemRow>
          <Circle>5</Circle>
          <Spacer size={8} />
          <NumberedItemColumn>
            <Heading>Covid Contract Addendums</Heading>
            <Spacer size={8} />
            <StyledText>
              Most states have created an addendum to existing contracts to address delays
              associated with COVID. If you are in need of an addendum, please contact us and we
              will provide the most recent one to you.
            </StyledText>
          </NumberedItemColumn>
        </NumberedItemRow>
      </MaxSectionWidthContainer>
    </Section>
    <Section background="transparentGreyAlpha25">
      <MaxSectionWidthContainer>
        <Heading>California</Heading>
        <Spacer size={8} />
        <StyledText>
          <StyledParagraph>
            As California enters phase 2, the California Association of Realtors recommends showings
            should be done virtually, whenever possible. Open houses are discouraged to the general
            public or on a walk in basis. Home owners are encouraged to use appointments or digital
            sign ins to control the amount of people. Many MLSs are still not allowing open houses.
            House tours are discouraged but allowed. Only a single agent and no more than 2 other
            adults are allowed in the home at a time. CA has created a new declaration that all
            attendees must sign to confirm their acknowledgement of the risk. This declaration,
            agreement amendments and the best practices guidelines provided to real estate agents
            has been placed in your listing dashboard in the documents section.
          </StyledParagraph>
          <StyledParagraph>
            You can see the California Association of Realtors Rules for Showings here:&nbsp;
            <StyledAnchor href="https://www.carcovidupdates.org/faq-showings">
              https://www.carcovidupdates.org/faq-showings
            </StyledAnchor>
          </StyledParagraph>
          {renderTourAppGraf()}
        </StyledText>
        <Spacer size={30} />
        <Heading>New York</Heading>
        <Spacer size={8} />
        <StyledText>
          <StyledParagraph>
            The Governor issued an Executive Order requiring all state residents to stay home until
            their county has passed beyond Phase 1. During this time, all in-person house tours are
            prohibited. The state has said that virtual tours are how showings should be conducted
            during Phase 1.
          </StyledParagraph>
          <StyledParagraph>
            Phase 2. During Phase 2 in person showings and open houses may be conducted. The
            following counties are now open to limited real estate activities in Phase 2. Open
            houses may only permit 1 party on the property at a time. Please see the NY Real Estate
            Guidance Document for full rules.
          </StyledParagraph>
        </StyledText>
        <Spacer size={30} />
        <StyledUnorderedList>
          <StyledListItem>
            <StyledListItemHeading>North Country</StyledListItemHeading>
            &nbsp;(Clinton, Essex, Franklin, Hamilton, Jefferson, Lewis, St. Lawrence).
          </StyledListItem>
          <StyledListItem>
            <StyledListItemHeading>Mohawk Valley</StyledListItemHeading>
            &nbsp;(Fulton, Herkimer, Montgomery, Oneida, Otsego, Schoharie).
          </StyledListItem>
          <StyledListItem>
            <StyledListItemHeading>Central NY</StyledListItemHeading>
            &nbsp;(Cayuga, Cortland, Madison, Onondaga, Oswego)
          </StyledListItem>
          <StyledListItem>
            <StyledListItemHeading>Finger Lakes</StyledListItemHeading>
            &nbsp;(Genesee, Livingston, Monroe, Ontario, Orleans, Seneca, Wayne, Wyoming, Yates).
          </StyledListItem>
          <StyledListItem>
            <StyledListItemHeading>Southern Tier</StyledListItemHeading>
            &nbsp;(Broome, Chemung, Chenango, Delaware, Schuyler, Steuben, Tioga, Tompkins).
          </StyledListItem>
          <StyledListItem>
            <StyledListItemHeading>Western NY</StyledListItemHeading>
            &nbsp;(Allegany, Cattaraugus, Chautauqua, Erie, Niagara).
          </StyledListItem>
          <StyledListItem>
            <StyledListItemHeading>Capital Region</StyledListItemHeading>
            &nbsp;(Albany, Columbia, Greene, Saratoga, Schenectady, Rensselaer, Warren, Washington).
          </StyledListItem>
          <StyledListItem>
            <StyledListItemHeading>Mid-Hudson</StyledListItemHeading>
            &nbsp;(Westchester, Rockland, Orange, Putnam, Dutchess, Sullivan, Ulster).
          </StyledListItem>
        </StyledUnorderedList>
        <Spacer size={30} />
        <LightItalicText uppercase>The NY Covid form is optional.</LightItalicText>
        <Spacer size={8} />
        <LightItalicText>{renderTourAppGraf()}</LightItalicText>
        <Spacer size={30} />
        <Heading>Connecticut</Heading>
        <Spacer size={8} />
        <StyledText>
          <StyledParagraph>
            The Governor is planning phase 2 of Connecticut&apos;s reopening in or around June 20.
            CT has defined real estate as an essential activity that may continue.
          </StyledParagraph>
          <StyledParagraph>
            The Connecticut Association of Realtors urges realtors to refrain from conducting broker
            open houses and public open houses until further notice. The following guidance has been
            provided: The following are prohibited: Open houses; Conducting any showing to a
            prospective buyer who is not likely to enter a contract in the next 30 days. Additional
            Guidance provided is to do the following: Showing a property exclusively to those making
            the decision, and not others. Restricting the opportunity for a showing to only those
            who would not proceed with a transaction without entering the property. Family members
            such as children, parents or friends should not be participating nor should any
            prospective buyer be provided an opportunity to view multiple properties unless you have
            certainty a contract may result in the next 30 days. Any individual without an essential
            need to enter a property for a transaction is not within the scope of
            &ldquo;essential.&rdquo; Only the inspector, the principle buyer(s) and their agent
            should be present. If there are a number of things the buyer would like repaired or done
            prior to closing, we encourage as much as possible that those be delivered in a buyer
            credit. While you may need to open the door for the appraiser and secure the property
            after the appraisal, you should refrain from entering the property with the appraiser.
          </StyledParagraph>
          {renderTourAppGraf()}
        </StyledText>
        <Spacer size={30} />
        <Heading>Florida</Heading>
        <Spacer size={8} />
        <StyledText>
          <StyledParagraph>
            Florida has now entered Phase 2. Real estate services were already deemed essential. The
            changes don&apos;t apply to the South Florida counties of Miami-Dade, Broward and Palm
            Beach, but the order allows some businesses in the counties to reopen &ldquo;after each
            county seeks approval with a written request from the County Mayor or if no mayor the
            County Administrator.&rdquo;
          </StyledParagraph>
          <StyledParagraph>
            As of May 22, 2020, in person house tours may resume. We strongly recommend if you
            decide to conduct an in person house tour, please follow&nbsp;
            <StyledAnchor href="https://www.cdc.gov/coronavirus/2019-ncov/prevent-getting-sick/prevention.html?CDC_AA_refVal=https%3A%2F%2Fwww.cdc.gov%2Fcoronavirus%2F2019-ncov%2Fprepare%2Fprevention.html">
              CDC preventative guidelines
            </StyledAnchor>
          </StyledParagraph>
          {renderTourAppGraf()}
        </StyledText>
        <Spacer size={30} />
        <Heading>Maryland</Heading>
        <Spacer size={8} />
        <StyledText>
          <StyledParagraph>
            The Governor plans to ease restrictions as the state enters phase 2 the weekend of June
            6. The state recommends limiting personal interactions, including open houses, to the
            greatest extent possible and engaging in-person only when necessary. They suggest
            following these strict protocols: • Except for open houses, in-person activities should
            be by appointment only. • No more than three (3) people, including the agent, may be at
            the property at any one time. • Those three persons must strictly follow physical
            distancing guidelines established by the Centers for Disease Control and Prevention
            (“CDC”) by remaining at least six feet apart at all times.
          </StyledParagraph>
          {renderTourAppGraf()}
        </StyledText>
        <Spacer size={30} />
        <Heading>Massachusetts</Heading>
        <Spacer size={8} />
        <StyledText>
          <StyledParagraph>
            There are no current orders in place against holding in person tours. The Massachusetts
            Association of Realtors has sent out guidance suggesting that the number of public open
            houses should be limited. While open houses are not prohibited, they are subject to the
            Governor’s order about limiting gatherings to ten people and the DPH advisory to stay
            home. If an open house is held, the number of people in the home should be limited to 10
            at a time and social distancing should be enforced. We recommend against having
            in-person house tours but if you do, please follow&nbsp;
            <StyledAnchor href="https://www.cdc.gov/coronavirus/2019-ncov/prevent-getting-sick/prevention.html?CDC_AA_refVal=https%3A%2F%2Fwww.cdc.gov%2Fcoronavirus%2F2019-ncov%2Fprepare%2Fprevention.html">
              CDC preventative guidelines
            </StyledAnchor>
          </StyledParagraph>
          {renderTourAppGraf()}
        </StyledText>
        <Spacer size={30} />
        <Heading>New Jersey</Heading>
        <Spacer size={8} />
        <StyledText>
          <StyledParagraph>
            The Governor has now lifted the Executive Order requiring all state residents to stay
            home. Showings by appointment are allowed. Realtors can operate and show houses to
            prospective buyers on a 1-on-1 basis. All agents and brokers must adhere to social
            distancing guidelines and ensure they are performing private one-on-one showings.&nbsp;
            <StyledAnchor href="https://www.cdc.gov/coronavirus/2019-ncov/prevent-getting-sick/prevention.html?CDC_AA_refVal=https%3A%2F%2Fwww.cdc.gov%2Fcoronavirus%2F2019-ncov%2Fprepare%2Fprevention.html">
              CDC preventative guidelines
            </StyledAnchor>
          </StyledParagraph>
          {renderTourAppGraf()}
        </StyledText>
        <Spacer size={30} />
        <Heading>Oregon</Heading>
        <Spacer size={8} />
        <StyledText>
          <StyledParagraph>
            As of June 5, 14 Oregon counties enter Phase 2 of Oregon’s Reopening, with an additional
            11 counties on June 6. Phase 2 Reopening allows for in-person gatherings of up to 50
            people indoors, it only does so with 6ft social distancing and strict sanitation
            protocols. Traditional open houses cannot guarantee 6ft distancing so only modified open
            houses, such as those by appointment only or those where one family tours the home at a
            time (maintaining a 6ft distance) and other individuals/families line up outside 6ft
            apart (consider marking off 6ft distances), would be allowed. Those hosting modified
            open houses should continue to maintain the strict sanitation protocols that they have
            been using throughout the COVID-19 pandemic and should review the Oregon Health
            Authority guidance on events/gatherings and on face coverings. Virtual open houses are
            still a good option. Below is the link from Oregon Association of Realtors detailing
            phase 2 real estate guidelines.
          </StyledParagraph>
          <StyledParagraph>
            <StyledAnchor href="https://oregonrealtors.org/wp-content/uploads/2020/06/OAR-Guide-for-Phase-2-Reopening-2.pdf">
              https://oregonrealtors.org/wp-content/uploads/2020/06/OAR-Guide-for-Phase-2-Reopening-2.pdf
            </StyledAnchor>
          </StyledParagraph>
          {renderTourAppGraf()}
        </StyledText>
        <Spacer size={30} />
        <Heading>Pennsylvania</Heading>
        <Spacer size={8} />
        <StyledText>
          <StyledParagraph>
            As of May 20 all real estate in Pennsylvania reopened with new guidance. Under these
            rules, in-person services can be provided under the same rules in red-phase counties as
            in yellow-phase counties. If a thing can be done it can be done anywhere in the state.
            If a thing can’t be done, it can’t be done anywhere in the state. Once counties move to
            green, many of the restrictions listed below will no longer be required. In person
            showing may not exceed 3 persons at one time. Masks must be worn. Gloves and booties are
            no longer required. All showing s should be spaced out with 30 minutes between them.
            Hosts must…maintain records of all appointments, including contact information for all
            participants.” Additional details in the guidance can be found on PAR on the attached
            link.&nbsp;
            <StyledAnchor href="https://www.parealtors.org/coronavirus">
              https://www.parealtors.org/coronavirus
            </StyledAnchor>
          </StyledParagraph>
          {renderTourAppGraf()}
        </StyledText>
        <Spacer size={30} />
        <Heading>Texas</Heading>
        <Spacer size={8} />
        <StyledText>
          <StyledParagraph>
            Gov. Abbott’s latest executive order (GA-18) states that businesses and people providing
            essential services should implement social distancing and telework where feasible,
            especially for people over the age of 65. Additionally, brokerages should now follow
            the&nbsp;
            <StyledAnchor href="https://www.dshs.state.tx.us/coronavirus/opentexas.aspx#protocols">
              minimum standard health protocols recommended by the Texas Department of State Health
              Services (DSHS)
            </StyledAnchor>
            —for example, training staff and agents on appropriate cleaning and disinfection, hand
            hygiene, and respiratory etiquette. Texas REALTORS® continues to recommend that members
            consider the advisability of holding open houses at this time, but if an in-person
            showing must take place, health and safety protocols should be in place. We strongly
            recommend if you decide to conduct an in person house tour, please follow&nsp;
            <StyledAnchor href="https://www.cdc.gov/coronavirus/2019-ncov/prevent-getting-sick/prevention.html?CDC_AA_refVal=https%3A%2F%2Fwww.cdc.gov%2Fcoronavirus%2F2019-ncov%2Fprepare%2Fprevention.html">
              CDC preventative guidelines
            </StyledAnchor>
          </StyledParagraph>
          {renderTourAppGraf()}
        </StyledText>
        <Spacer size={30} />
        <Heading>Utah</Heading>
        <Spacer size={8} />
        <StyledText>
          <StyledParagraph>
            Utah is the 2nd phase of repoening. At this time there are no orders prohibiting in
            person tours. Here is a link to the national recommendations from the National
            Association of Realtors regarding Showing Guidance:&nbsp;
            <StyledAnchor href="https://www.nar.realtor/sites/default/files/documents/Showing-Guidance-During-COVID-05-14-2020.pdf">
              https://www.nar.realtor/sites/default/files/documents/Showing-Guidance-During-COVID-05-14-2020.pdf.
            </StyledAnchor>
          </StyledParagraph>
          {renderTourAppGraf()}
        </StyledText>
        <Spacer size={30} />
        <Heading>Washington D.C.</Heading>
        <Spacer size={8} />
        <StyledText>
          <StyledParagraph>
            It is advised to limit personal interactions, including open houses, to the greatest
            extent possible and will engage in-person only when necessary, and follow these strict
            protocols: • Except for open houses, in-person activities should be by appointment only.
            • No more than three (3) people, including the agent, may be at the property at any one
            time. • Those three persons must strictly follow physical distancing guidelines
            established by the Centers for Disease Control and Prevention (“CDC”) by remaining at
            least six feet apart at all times.
          </StyledParagraph>
          {renderTourAppGraf()}
        </StyledText>
      </MaxSectionWidthContainer>
    </Section>
    <Section>
      <MaxSectionWidthContainer>
        <Heading>FHA and VA Loans</Heading>
        <Spacer size={8} />
        <StyledText>
          <StyledParagraph>
            Due to the COVID-19 pandemic, there have been some changes to government backed loan
            programs that you should be aware of. Here are a summary of these changes below:
          </StyledParagraph>
          <StyledParagraph>
            FHA – Credit (effective immediately for cases closed on or before May 17, 2020
          </StyledParagraph>
        </StyledText>
        <Spacer size={30} />
        <StyledUnorderedList>
          <StyledListItem>
            Reverification of employment prior to closing – additional paystub (current) or bank
            statement showing the payroll deposits.
          </StyledListItem>
          <StyledListItem>
            Purchase transactions require 2 months cash reserves (PITIA).
          </StyledListItem>
        </StyledUnorderedList>
        <Spacer size={30} />
        <StyledText>
          <StyledParagraph>
            FHA - Appraisals - If traditional appraisal is not available (effective immediately for
            appraisal inspections completed on or before May 17, 2020)
          </StyledParagraph>
        </StyledText>
        <Spacer size={30} />
        <StyledUnorderedList>
          <StyledListItem>
            Exterior inspection on 1004 for Purchase and Rate and Term Refi’s
          </StyledListItem>
          <StyledListItem>
            Traditional appraisal required for Cash out refi’s and 203K’s.
          </StyledListItem>
        </StyledUnorderedList>
        <Spacer size={30} />
        <StyledText>
          <StyledParagraph>
            VA/USDA – Credit (effective immediately for all loans closed on, or after, March 27,
            2020 until further notice)
          </StyledParagraph>
        </StyledText>
        <Spacer size={30} />
        <StyledUnorderedList>
          <StyledListItem>
            Reverification of employment prior to closing – additional paystub (current) or bank
            statement showing the payroll deposits.
          </StyledListItem>
        </StyledUnorderedList>
        <Spacer size={30} />
        <StyledText>
          <StyledParagraph>Overlays - Credit</StyledParagraph>
        </StyledText>
        <Spacer size={30} />
        <StyledUnorderedList>
          <StyledListItem>640 Minimum FICO required</StyledListItem>
          <StyledListItem>
            641-659 scores will have a 43% maximum debt ratio. 43.01% – 50.00% debt ratios are
            acceptable with 3 months cash reserves. Gifts are not acceptable.
          </StyledListItem>
          <StyledListItem>
            Reverification of employment must confirm that work hours have not been reduced.
          </StyledListItem>
        </StyledUnorderedList>
        <Spacer size={30} />
        <StyledText>
          <StyledParagraph>
            VA - Appraisals – If traditional appraisal is not available
          </StyledParagraph>
        </StyledText>
        <Spacer size={30} />
        <StyledUnorderedList>
          <StyledListItem>
            VA is responsible for the appraiser and the forms so they will order the appropriate
            appraisal form.
          </StyledListItem>
        </StyledUnorderedList>
      </MaxSectionWidthContainer>
    </Section>
    <FooterContainer>
      <MaxWidthContainer>
        <ExtraLargeHeader
          title="HomeLister is Ready!"
          subtitle="HomeLister started as an online hybrid brokerage, taking the real estate industry into the 21st century by using technology to enable you to control your home sale and reach as many buyers as possible. We are just as committed to doing that now. We are ready to help guide you on the ins and outs of selling your house during these difficult times. If you have questions, just reach out to us and ask."
          textColor="white"
          noMargin
        />
      </MaxWidthContainer>
    </FooterContainer>
  </>
)

export default CovidPage
